export function getLineQuantity(entry) {
  const quantity = Number(entry?.quantity ?? 1);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
}

export function getExtraLineQuantity(extra) {
  return getLineQuantity(extra);
}

export function getMenuOptionLineQuantity(option) {
  return getLineQuantity(option);
}

export function getExtraUnitPrice(extra) {
  return Number(extra?.price ?? extra?.Price ?? 0);
}

export function getMenuOptionUnitPrice(option) {
  return Number(
    option?.optionPrice ??
      option?.OptionPrice ??
      option?.price ??
      option?.Price ??
      0,
  );
}

export function formatQuantityLabel(name, quantity) {
  const label = name || "Item";
  if (quantity <= 1) return label;
  return `${label} x${quantity}`;
}

export function formatExtraLabel(extra) {
  return formatQuantityLabel(
    extra?.name || extra?.Name || "Add-on",
    getExtraLineQuantity(extra),
  );
}

export function formatMenuOptionLabel(option) {
  return formatQuantityLabel(
    option?.optionName || option?.OptionName || "Option",
    getMenuOptionLineQuantity(option),
  );
}

function normalizeMenuOptionEntry(option = {}) {
  return {
    optionName: option.optionName || option.OptionName || "Option",
    optionPrice: getMenuOptionUnitPrice(option),
    menuOptionId:
      option.menuOptionId || option.MenuOptionId || option.id || option.Id || null,
    quantity: getMenuOptionLineQuantity(option) || Number(option.quantity ?? 1),
  };
}

/**
 * Normalize an order / cart line so bill helpers work for both
 * camelCase API payloads and PascalCase responses.
 */
export function normalizeOrderItem(item = {}) {
  const orderItemOptionRaw =
    item.orderItemOption || item.OrderItemOption || null;
  const orderItemExtrasRaw =
    item.orderItemExtras || item.OrderItemExtras || [];
  const orderItemMenuOptionsRaw =
    item.orderItemMenuOptions || item.OrderItemMenuOptions || [];

  return {
    ...item,
    id: item.id || item.Id || null,
    itemName: item.itemName || item.ItemName || "Item",
    itemPrice: Number(item.itemPrice ?? item.ItemPrice ?? 0),
    menuId: item.menuId || item.MenuId || null,
    quantity: Number(item.quantity ?? item.Quantity ?? 1),
    description: item.description || item.Description || "",
    orderItemMenuOptions: Array.isArray(orderItemMenuOptionsRaw)
      ? orderItemMenuOptionsRaw.map(normalizeMenuOptionEntry)
      : [],
    orderItemOption: orderItemOptionRaw
      ? {
          id: orderItemOptionRaw.id || orderItemOptionRaw.Id || null,
          optionName:
            orderItemOptionRaw.optionName ||
            orderItemOptionRaw.OptionName ||
            "Option",
          optionPrice: Number(
            orderItemOptionRaw.optionPrice ??
              orderItemOptionRaw.OptionPrice ??
              0,
          ),
          menuOptionId:
            orderItemOptionRaw.menuOptionId ||
            orderItemOptionRaw.MenuOptionId ||
            null,
        }
      : null,
    orderItemExtras: Array.isArray(orderItemExtrasRaw)
      ? orderItemExtrasRaw.map((extra) => ({
          id: extra?.id || extra?.Id || null,
          name: extra?.name || extra?.Name || "Add-on",
          price: getExtraUnitPrice(extra),
          extrasId: extra?.extrasId || extra?.ExtrasId || null,
          quantity: extra?.quantity ?? extra?.Quantity ?? 1,
        }))
      : [],
  };
}

export function buildOrderItemOptionSnapshot(menuOptions = []) {
  const selected = (menuOptions || []).filter(
    (option) => getMenuOptionLineQuantity(option) > 0,
  );

  if (selected.length !== 1) return null;

  const option = selected[0];
  if (getMenuOptionLineQuantity(option) !== 1) return null;

  return {
    optionName: option.optionName || option.OptionName || "Option",
    optionPrice: getMenuOptionUnitPrice(option),
    menuOptionId: option.menuOptionId || option.id || option.Id || null,
  };
}

export function getSelectedMenuOptions(item) {
  const normalized = normalizeOrderItem(item);

  const fromCart = (normalized.orderItemMenuOptions || []).filter(
    (option) => getMenuOptionLineQuantity(option) > 0,
  );

  if (fromCart.length > 0) {
    return fromCart;
  }

  if (normalized.orderItemOption?.optionName) {
    return [
      {
        optionName: normalized.orderItemOption.optionName,
        optionPrice: Number(normalized.orderItemOption.optionPrice || 0),
        menuOptionId: normalized.orderItemOption.menuOptionId,
        quantity: 1,
      },
    ];
  }

  return [];
}

export function getSelectedExtras(item) {
  const normalized = normalizeOrderItem(item);

  if (
    !Array.isArray(normalized.orderItemExtras) ||
    normalized.orderItemExtras.length === 0
  ) {
    return [];
  }

  const hasExplicitQuantity = normalized.orderItemExtras.some(
    (extra) => extra?.quantity != null,
  );

  if (hasExplicitQuantity) {
    return normalized.orderItemExtras.filter(
      (extra) => getExtraLineQuantity(extra) > 0,
    );
  }

  return normalized.orderItemExtras.map((extra) => ({
    ...extra,
    name: extra?.name || extra?.Name || "Add-on",
    price: getExtraUnitPrice(extra),
    quantity: 1,
  }));
}

function addGroupedBillLine(groups, kind, name, unitPrice, quantity) {
  const qty = getLineQuantity({ quantity });
  if (qty <= 0) return;

  const label = name || (kind === "option" ? "Option" : "Add-on");
  const price = Number(unitPrice || 0);
  const key = `${kind}|${label}|${price}`;
  const existing = groups.get(key);

  if (existing) {
    existing.quantity += qty;
    return;
  }

  groups.set(key, {
    kind,
    name: label,
    price,
    quantity: qty,
  });
}

function collectPerUnitBillComponents(item) {
  const normalized = normalizeOrderItem(item);
  const groups = new Map();
  const cartOptions = getSelectedMenuOptions(normalized);
  const hasCartMenuOptions = cartOptions.length > 0;

  cartOptions.forEach((option) => {
    addGroupedBillLine(
      groups,
      "option",
      option.optionName || option.OptionName,
      getMenuOptionUnitPrice(option),
      getMenuOptionLineQuantity(option),
    );
  });

  getSelectedExtras(normalized).forEach((extra) => {
    if (hasCartMenuOptions && !(extra.extrasId || extra.ExtrasId)) {
      return;
    }

    addGroupedBillLine(
      groups,
      extra.extrasId || extra.ExtrasId ? "extra" : "option",
      extra.name || extra.Name,
      getExtraUnitPrice(extra),
      getExtraLineQuantity(extra),
    );
  });

  return Array.from(groups.values());
}

/**
 * Grouped menu options + extras for one configured unit of an order line.
 * Each entry includes quantity so duplicates collapse into one row.
 */
export function getBillDetailLines(item) {
  return collectPerUnitBillComponents(item);
}

/**
 * Rows for bill tables: base item + grouped menu options / add-ons.
 */
export function getItemBillRows(item) {
  const normalized = normalizeOrderItem(item);
  const unitPrice = Number(normalized.itemPrice || 0);
  const orderQuantity = Number(normalized.quantity || 1);

  const rows = [
    {
      key: "base",
      label: normalized.itemName,
      sublabel: "Base item",
      price: unitPrice,
      quantity: orderQuantity,
      lineTotal: unitPrice * orderQuantity,
    },
  ];

  collectPerUnitBillComponents(normalized).forEach((line) => {
    const quantity = line.quantity * orderQuantity;
    rows.push({
      key: `${line.kind}-${line.name}-${line.price}`,
      label: line.name,
      sublabel: line.kind === "option" ? "Menu option" : "Add-on",
      price: line.price,
      quantity,
      lineTotal: line.price * quantity,
    });
  });

  return rows;
}

export function getOrderLineUnitPrice(item) {
  const normalized = normalizeOrderItem(item);
  const basePrice = Number(normalized.itemPrice || 0);

  const detailTotal = collectPerUnitBillComponents(normalized).reduce(
    (sum, line) => sum + Number(line.price || 0) * Number(line.quantity || 0),
    0,
  );

  return basePrice + detailTotal;
}

export function getOrderLineTotal(item) {
  const normalized = normalizeOrderItem(item);
  return getOrderLineUnitPrice(normalized) * Number(normalized.quantity || 1);
}

export function flattenExtrasForApi(extras) {
  if (!Array.isArray(extras)) return [];

  return extras.flatMap((extra) => {
    const quantity = getExtraLineQuantity(extra);
    if (quantity <= 0) return [];

    const unitPrice = getExtraUnitPrice(extra);
    const name = extra.name || extra.Name || "";
    const extrasId = extra.extrasId || extra.ExtrasId || null;

    return Array.from({ length: quantity }, () => {
      const row = {
        id: null,
        name,
        price: unitPrice,
        orderItemId: null,
      };

      if (extrasId) {
        row.extrasId = extrasId;
      }

      return row;
    });
  });
}

export function flattenMenuOptionsForApi(menuOptions) {
  if (!Array.isArray(menuOptions)) return [];

  return menuOptions.flatMap((option) => {
    const quantity = getMenuOptionLineQuantity(option);
    if (quantity <= 0) return [];

    const unitPrice = getMenuOptionUnitPrice(option);
    const name = option.optionName || option.OptionName || "Option";

    return Array.from({ length: quantity }, () => ({
      id: null,
      name,
      price: unitPrice,
      orderItemId: null,
    }));
  });
}

/**
 * Always flatten selected menu options into OrderItemExtras so every
 * option appears as its own priced line after the order is saved.
 */
export function buildOrderItemExtrasPayload(item) {
  const selectedOptions = Array.isArray(item?.orderItemMenuOptions)
    ? item.orderItemMenuOptions
    : Array.isArray(item?.OrderItemMenuOptions)
      ? item.OrderItemMenuOptions
      : [];

  return [
    ...flattenMenuOptionsForApi(selectedOptions),
    ...flattenExtrasForApi(item?.orderItemExtras || item?.OrderItemExtras),
  ];
}
